import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal, Overline } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { IMAGES } from "../lib/constants";

const AboutPage = () => {
  useSEO(
    "About Us | Kay's Carpet Cleaning Gold Coast",
    "Kay's Carpet Cleaning is a Gold Coast local cleaning business with 7+ years of hands-on experience and thousands of completed residential and commercial jobs. Call 0450 311 907."
  );

  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Us"
        title="Cleaning Gold Coast Homes & Businesses for"
        titleItalic="More Than 10 Years"
        text="Kay's Carpet Cleaning is a local Gold Coast cleaning business built on practical experience, careful work and straight answers."
        testId="about-hero"
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-14 items-start">
        <Reveal className="lg:col-span-5">
          <div className="overflow-hidden rounded-tl-[4rem] rounded-br-[4rem] rounded-tr-2xl rounded-bl-2xl border border-brand-line/60 shadow-[0_30px_80px_rgba(45,90,83,0.14)]">
            <img
              src={IMAGES.cleaner}
              alt="Kay's Carpet Cleaning professional cleaning equipment in use"
              loading="lazy"
              className="w-full h-[340px] sm:h-[460px] object-cover"
              data-testid="about-page-image"
            />
          </div>
        </Reveal>

        <div className="lg:col-span-7 lg:pl-6">
          <Reveal>
            <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink">
              Experience you can talk to
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
              Over more than 7 years, Kay's Carpet Cleaning has completed thousands of
              professional cleaning jobs across the Gold Coast — carpets, couches, rugs,
              mattresses, tiled areas, offices and commercial spaces. That experience shows
              up in the details: knowing how different fibres respond, when a stain is
              treatable and when it has permanently marked the material, and how to clean
              thoroughly without over-wetting or over-promising.
            </p>
            <p className="mt-4 text-base sm:text-lg text-brand-muted leading-relaxed">
              We are deliberately not a big anonymous franchise. When you contact Kay's Carpet
              Cleaning, you deal directly with a local operator who will look at your actual
              job, explain your options in plain language, and give you an honest estimate
              based on what your cleaning genuinely requires.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <h3 className="mt-10 font-display font-medium text-2xl tracking-tight text-brand-ink">How we work</h3>
            <ul className="mt-5 space-y-3">
              {[
                "Assess first — material, condition and staining, before any cleaning begins",
                "Choose the method that suits the job, not a one-size-fits-all approach",
                "Set honest expectations — including when a stain may not fully lift",
                "Treat every home and workplace with care and respect",
                "Provide free, no-obligation estimates based on your actual requirements",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-base text-brand-muted leading-relaxed">
                  <CheckCircle2 size={18} className="text-brand shrink-0 mt-1" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 bg-brand-mist/50 border border-brand-line/60 rounded-2xl px-7 py-6">
              <p className="text-base sm:text-lg text-brand-ink font-medium leading-relaxed">
                Have a cleaning problem you're not sure about? Call us and tell us what you
                need — a quick conversation is often all it takes to know whether we can help.
              </p>
              <Link to="/get-estimate" data-testid="about-estimate-link" className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                Get an Estimate <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        <CTABand id="about-final" heading="Tell Us What You Need Cleaned" text="Carpets, couches, rugs, mattresses, tiles or something else — send us the details and we'll provide an estimate." primaryLabel="Get My Estimate" />
      </div>
    </div>
  );
};

export default AboutPage;
